// =============================================================================
// GEOTOKEN — one structured, selectable location token that de-hardcodes "where"
// across the platform. A GeoToken carries a real ISO-3166 country, so the
// work-eligibility engine reads a country directly instead of guessing one from
// a free-text city regex (lib/eligibility.ts). It is the single source of truth
// the three free-text location fields (home_location, target_markets,
// scenarios.locations) DERIVE from.
//
// This is NOT a Drizzle table (same posture as temporal.ts / resume_document.ts).
// It is the shared CONTRACT + a pure, dependency-free core, exported from the
// schema package so apps/api and apps/web compute the IDENTICAL location facts —
// no drift between a server signal and a client badge.
//
// Two hard design rules keep it droppable ANYWHERE:
//   1. No I/O, no DB, no LLM. Consumers feed plain strings (ids / free text) and
//      get back GeoTokens or ISO country codes. Every function is deterministic.
//   2. MINIMAL seed on purpose. GEO_SEED covers exactly the tokens already implied
//      by inferTargetCountry's regex plus the two remote tokens — so behaviour is
//      a STRICT SUPERSET of today, never a regression. This is the socket; a full
//      world catalogue + a picker UI come later, on top of this contract.
//
// Slug ids (e.g. 'au-bne', 'remote-us'), not UUIDs — legible, debuggable, and a
// soft contract once stored. `country` is ISO-3166 alpha-2 — the key the visa
// matrix (A1) reads. The visa research refines what's in a matrix cell, not the
// shape of a GeoToken, which is why this foundation is stable to lock now.
// =============================================================================

// ─────────────────────────────────────────────────────────────────────────────
// The contract
// ─────────────────────────────────────────────────────────────────────────────

/** One structured, selectable location. `id` is a human-readable slug; `country`
 *  is ISO-3166 alpha-2 (the matrix key). `remote` marks the remote-* tokens. */
export interface GeoToken {
  /** Human-readable slug — 'au-bne','au-syd','us-mia','remote-au','remote-us'. */
  id: string;
  /** Display label — 'Brisbane, AU'. */
  label: string;
  /** ISO-3166 alpha-2 — 'AU','US','GB','CA'. The work-eligibility matrix key. */
  country: string;
  /** State/region, where it sharpens the token. */
  region?: string;
  /** City name, where the token is city-scoped. */
  city?: string;
  /** True for the remote-* tokens (country-scoped, no city). */
  remote?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Seed — MINIMAL by design: exactly the tokens already implied by
// inferTargetCountry's regex (lib/eligibility.ts) plus the two remote tokens.
// Keep behaviour a strict superset of today; do NOT grow this into a world
// catalogue here (that's a later slice with its own picker UI).
// ─────────────────────────────────────────────────────────────────────────────

export const GEO_SEED: GeoToken[] = [
  // Australia — the regex's brisbane|sydney|melbourne|perth.
  { id: 'au-bne', label: 'Brisbane, AU', country: 'AU', region: 'QLD', city: 'Brisbane' },
  { id: 'au-syd', label: 'Sydney, AU', country: 'AU', region: 'NSW', city: 'Sydney' },
  { id: 'au-mel', label: 'Melbourne, AU', country: 'AU', region: 'VIC', city: 'Melbourne' },
  { id: 'au-per', label: 'Perth, AU', country: 'AU', region: 'WA', city: 'Perth' },
  // United States — the regex's miami|new york|san francisco|austin|seattle.
  { id: 'us-mia', label: 'Miami, US', country: 'US', region: 'FL', city: 'Miami' },
  { id: 'us-nyc', label: 'New York, US', country: 'US', region: 'NY', city: 'New York' },
  {
    id: 'us-sf',
    label: 'San Francisco, US',
    country: 'US',
    region: 'CA',
    city: 'San Francisco',
  },
  { id: 'us-aus', label: 'Austin, US', country: 'US', region: 'TX', city: 'Austin' },
  { id: 'us-sea', label: 'Seattle, US', country: 'US', region: 'WA', city: 'Seattle' },
  // United Kingdom — the regex's london|england.
  { id: 'gb-lon', label: 'London, GB', country: 'GB', region: 'England', city: 'London' },
  // Canada — the regex's toronto|vancouver.
  { id: 'ca-tor', label: 'Toronto, CA', country: 'CA', region: 'ON', city: 'Toronto' },
  { id: 'ca-van', label: 'Vancouver, CA', country: 'CA', region: 'BC', city: 'Vancouver' },
  // Remote — country-scoped, no city.
  { id: 'remote-au', label: 'Remote (AU)', country: 'AU', remote: true },
  { id: 'remote-us', label: 'Remote (US)', country: 'US', remote: true },
];

/** Indexed once for O(1) lookups; the seed is the only source so this stays in
 *  sync by construction. */
const SEED_BY_ID: Map<string, GeoToken> = new Map(GEO_SEED.map((t) => [t.id, t]));

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────────────────

/** The seeded token for an id, or null when the id is unknown. */
export function geoById(id: string | null | undefined): GeoToken | null {
  if (!id) return null;
  return SEED_BY_ID.get(id) ?? null;
}

/** The ISO-3166 alpha-2 country for a token id, or null when un-mappable. This
 *  is the derivation the visa matrix (A1) and the eligibility socket read. */
export function countryOf(id: string | null | undefined): string | null {
  return geoById(id)?.country ?? null;
}

/** True when the id is a remote-* token. Unknown ids are not remote. */
export function isRemote(id: string | null | undefined): boolean {
  return geoById(id)?.remote === true;
}

/** Deduped ISO countries for a list of token ids (order preserved by first
 *  appearance). Un-mappable ids drop out silently. */
export function tokensToCountries(ids: string[] | null | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of ids ?? []) {
    const country = countryOf(id);
    if (country && !seen.has(country)) {
      seen.add(country);
      out.push(country);
    }
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy normalisation — best-effort free-text → GeoToken id. REUSES the exact
// token cues from inferTargetCountry's regex (lib/eligibility.ts) so a string
// that used to infer a country now resolves to a concrete city/remote token.
// Returns null when nothing matches, so the caller KEEPS the legacy string
// (no data loss, behaviour unchanged for un-mapped input).
// ─────────────────────────────────────────────────────────────────────────────

/** City/remote cues → seeded token id, in match-precedence order. The cues are
 *  the same ones inferTargetCountry keys off, so this never maps a string the
 *  regex would have read as a different country. Remote cues are checked first
 *  so "Remote US" → 'remote-us' rather than the bare-country fallthrough. */
const LEGACY_CUES: Array<{ test: RegExp; id: string }> = [
  // Remote first (more specific than a bare country word).
  { test: /\bremote\s*au\b|\bau\s*remote\b|remote\s*\(?\s*au/i, id: 'remote-au' },
  {
    test: /\bremote\s*us\b|\bus\s*remote\b|remote\s*\(?\s*us|remote\s*united states/i,
    id: 'remote-us',
  },
  // Australia cities (regex: brisbane|sydney|melbourne|perth).
  { test: /\bbrisbane\b/i, id: 'au-bne' },
  { test: /\bsydney\b/i, id: 'au-syd' },
  { test: /\bmelbourne\b/i, id: 'au-mel' },
  { test: /\bperth\b/i, id: 'au-per' },
  // United States cities (regex: miami|new york|san francisco|austin|seattle).
  { test: /\bmiami\b/i, id: 'us-mia' },
  { test: /\bnew york\b/i, id: 'us-nyc' },
  { test: /\bsan francisco\b/i, id: 'us-sf' },
  { test: /\baustin\b/i, id: 'us-aus' },
  { test: /\bseattle\b/i, id: 'us-sea' },
  // United Kingdom (regex: london|england; plus the bare-country uk cue).
  { test: /\blondon\b/i, id: 'gb-lon' },
  { test: /\bengland\b/i, id: 'gb-lon' },
  // Canada (regex: toronto|vancouver).
  { test: /\btoronto\b/i, id: 'ca-tor' },
  { test: /\bvancouver\b/i, id: 'ca-van' },
  // Bare-country words map to the canonical city for that country, so a string
  // like "Australia" still yields a real token (its country resolves correctly).
  { test: /\bau\b|austral/i, id: 'au-bne' },
  { test: /\buk\b|united kingdom/i, id: 'gb-lon' },
  { test: /canada/i, id: 'ca-tor' },
  // 'us'/'united states' last: the narrower US cities + remote-us win first.
  { test: /\bus\b|united states/i, id: 'us-mia' },
];

/** Best-effort: map a free-text location to a seeded GeoToken id, or null when
 *  un-mappable (caller keeps the legacy string). Reuses inferTargetCountry's
 *  token cues so it never disagrees with the existing country inference. */
export function normalizeLegacyLocation(free: string | null | undefined): string | null {
  const s = (free ?? '').trim();
  if (!s) return null;
  for (const cue of LEGACY_CUES) {
    if (cue.test.test(s)) return cue.id;
  }
  return null;
}
